// ✅ src/components/screens/AddProductScreen.js
// CLEAN Add Product UX (UPDATED):
// ✅ Uploads to Firebase Storage (multi images)
// ✅ Preview images + pick thumbnail + remove image
// ✅ Seller categories: stores/{uid}/categories
// ✅ Seller-defined productType (free text)
// ✅ Discount (toggle + percent OR final price)
// ✅ Custom fields: seller adds key/value rows (NO presets)
// ✅ Saves to products
// ✅ FIX: On mobile, dropdowns use in-app Bottom Sheet (no native iOS popup)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db, storage } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function moneyToNumber(v) {
  const n = Number(String(v || "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function numToInt(v) {
  const n = Number(String(v ?? "").replace(/[^\d]/g, ""));
  return Number.isFinite(n) ? n : 0;
}
function uid2() {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

/**
 * ✅ Normalize common attribute keys so Product Detail page can rely on:
 * attributes.Color and attributes.Size and attributes.Condition
 */
function normalizeAttributes(mapLike) {
  const out = {};
  const raw = mapLike || {};

  for (const k of Object.keys(raw)) {
    const keyRaw = safe(k);
    const val = safe(raw[k]);
    if (!keyRaw || !val) continue;

    const lower = keyRaw.toLowerCase().trim();
    const cleaned = lower.replace(/[^a-z0-9 ]/g, "").replace(/\s+/g, " ").trim();

    // ✅ Color variants: color, colors, colour, colours, color(s)
    if (
      cleaned === "color" ||
      cleaned === "colors" ||
      cleaned === "colour" ||
      cleaned === "colours" ||
      cleaned === "color s" ||
      cleaned === "colour s"
    ) {
      out["Color"] = val;
      continue;
    }

    // ✅ Size variants
    if (cleaned === "size" || cleaned === "sizes") {
      out["Size"] = val;
      continue;
    }

    // ✅ Condition variants
    if (
      cleaned === "condition" ||
      cleaned === "item condition" ||
      cleaned === "itemcondition"
    ) {
      out["Condition"] = val;
      continue;
    }

    // default: keep seller key (trimmed)
    const key = keyRaw.slice(0, 40);
    out[key] = val.slice(0, 200);
  }

  return out;
}

/* ---------------- mobile select helpers ---------------- */
function useIsMobile(breakpoint = 520) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpoint;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function MobileSheetSelect({
  label,
  value,
  onChange,
  options, // [{value,label}]
  disabled = false,
  placeholder = "Select…",
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel =
    options.find((o) => String(o.value) === String(value))?.label || "";

  const close = () => setOpen(false);

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {label ? <div style={styles.labelText}>{label}</div> : null}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{
          ...styles.input,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: disabled ? "#f1f5f9" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span style={{ color: selectedLabel ? "#0f172a" : "#64748b" }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ fontWeight: 1000 }}>▾</span>
      </button>

      {open ? (
        <div
          onClick={close}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              background: "#fff",
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
              padding: 12,
              borderTop: "1px solid #e7ecf6",
              boxShadow: "0 -20px 60px rgba(15,23,42,0.15)",
              maxHeight: "70vh",
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div style={{ fontWeight: 1000 }}>{label || "Select"}</div>
              <button type="button" onClick={close} style={styles.smallBtn}>
                Close
              </button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {options.map((opt) => {
                const active = String(opt.value) === String(value);
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      close();
                    }}
                    style={{
                      border: "1px solid #e7ecf6",
                      background: active ? "#0f172a" : "#fff",
                      color: active ? "#fff" : "#0f172a",
                      borderRadius: 12,
                      padding: "12px 12px",
                      fontWeight: 950,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function AddProductScreen({ goBack }) {
  const fileRef = useRef(null);
  const isMobile = useIsMobile(520);

  /** @type {[import("firebase/auth").User|null, Function]} */
  const [user, setUser] = useState(null);

  /** @type {[any, Function]} */
  const [store, setStore] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // categories
  const [catsLoading, setCatsLoading] = useState(false);
  const [categories, setCategories] = useState([]); // [{id,name,order}]
  const [categoryId, setCategoryId] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");

  // product fields
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("1");
  const [description, setDescription] = useState("");
  const [currency, setCurrency] = useState("GHS");
  const [status, setStatus] = useState("ACTIVE");
  const [productType, setProductType] = useState("");

  // discount
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountMode, setDiscountMode] = useState("PERCENT"); // PERCENT | FINAL_PRICE
  const [discountPercent, setDiscountPercent] = useState("0");
  const [discountFinalPrice, setDiscountFinalPrice] = useState("");

  // ✅ seller custom fields (no presets)
  const [attributes, setAttributes] = useState([{ key: "", value: "" }]);

  // images
  // images: [{url, path, name}]
  const [images, setImages] = useState([]);
  const [thumbIndex, setThumbIndex] = useState(0);

  // upload state
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  /* ---------------- auth ---------------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setLoading(false);
        setMsg("Please login first.");
      }
    });
    return () => unsub();
  }, []);

  /* ---------------- load store ---------------- */
  useEffect(() => {
    if (!user) return;
    let alive = true;

    (async () => {
      setLoading(true);
      setMsg("");

      try {
        const snap = await getDoc(doc(db, "stores", user.uid));
        if (!snap.exists()) {
          if (!alive) return;
          setStore(null);
          setMsg("No store found for this account.");
        } else {
          if (!alive) return;
          setStore({ id: user.uid, ...(snap.data() || {}) });
        }
      } catch (e) {
        if (!alive) return;
        setMsg(e?.message || "Failed to load store.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user]);

  /* ---------------- load categories (seller-owned) ---------------- */
  useEffect(() => {
    if (!user) return;
    let alive = true;

    (async () => {
      setCatsLoading(true);
      try {
        const q = query(
          collection(db, "stores", user.uid, "categories"),
          orderBy("order", "asc")
        );
        const snap = await getDocs(q);
        if (!alive) return;

        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setCategories(list);

        // auto pick first category if none selected
        if (!categoryId && list.length) setCategoryId(list[0].id);
      } catch {
        if (!alive) return;
        setCategories([]);
      } finally {
        if (alive) setCatsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // keep thumbnail index safe when images change
  useEffect(() => {
    if (!images.length) {
      setThumbIndex(0);
      return;
    }
    if (thumbIndex > images.length - 1) setThumbIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length]);

  const storeName = useMemo(
    () => safe(store?.storeName) || safe(user?.displayName) || "Your Store",
    [store, user]
  );

  const computedDiscountPercent = useMemo(() => {
    const p = moneyToNumber(price);
    if (!discountEnabled) return 0;

    if (discountMode === "PERCENT") {
      const dp = Math.max(0, Math.min(95, numToInt(discountPercent)));
      return dp;
    }

    // FINAL_PRICE mode → convert to percent
    const fp = moneyToNumber(discountFinalPrice);
    if (!p || p <= 0) return 0;
    if (!fp || fp <= 0) return 0;
    if (fp >= p) return 0;

    const pct = Math.round(((p - fp) / p) * 100);
    return Math.max(0, Math.min(95, pct));
  }, [price, discountEnabled, discountMode, discountPercent, discountFinalPrice]);

  const cleanedAttributes = useMemo(() => {
    const map = {};
    for (const row of attributes) {
      const k = safe(row.key);
      const v = safe(row.value);
      if (!k || !v) continue;
      map[k.slice(0, 40)] = v.slice(0, 200);
    }
    return map;
  }, [attributes]);

  const normalizedAttributes = useMemo(
    () => normalizeAttributes(cleanedAttributes),
    [cleanedAttributes]
  );

  const categoryOptions = useMemo(() => {
    if (!categories.length) {
      return [
        {
          value: "",
          label: catsLoading ? "Loading…" : "No categories yet (add one below)",
        },
      ];
    }
    return categories.map((c) => ({
      value: c.id,
      label: safe(c.name) || c.id,
    }));
  }, [categories, catsLoading]);

  /* ---------------- image upload ---------------- */
  const openPicker = () => {
    if (uploading) return;
    fileRef.current?.click();
  };

  const uploadFiles = async (files) => {
    if (!user) return;
    if (!files?.length) return;

    setUploading(true);
    setUploadPct(0);
    setMsg("");

    try {
      const uploaded = [];

      // sequential uploads for stable progress
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const ext = safe(f.name.split(".").pop()).toLowerCase() || "jpg";
        const path = `stores/${user.uid}/products/${uid2()}.${ext}`;
        const r = ref(storage, path);

        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(r, f);

          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round(
                (snap.bytesTransferred / snap.totalBytes) * 100
              );
              const overall = Math.round(((i + pct / 100) / files.length) * 100);
              setUploadPct(overall);
            },
            (err) => reject(err),
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              uploaded.push({ url, path, name: f.name });
              resolve();
            }
          );
        });
      }

      setImages((prev) => {
        const next = [...prev, ...uploaded];
        return next;
      });

      setMsg("✅ Images uploaded.");
    } catch (e) {
      setMsg(e?.message || "Upload failed. Try again.");
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    await uploadFiles(files);
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  /* ---------------- custom fields ---------------- */
  const addAttrRow = () =>
    setAttributes((prev) => [...prev, { key: "", value: "" }]);

  const removeAttrRow = (idx) =>
    setAttributes((prev) => prev.filter((_, i) => i !== idx));

  const updateAttrRow = (idx, patch) =>
    setAttributes((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    );

  /* ---------------- categories ---------------- */
  const createCategory = async () => {
    if (!user) return;
    const name = safe(newCategoryName);
    if (!name) return setMsg("Enter a category name.");

    setSaving(true);
    setMsg("");

    try {
      const id = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "")
        .slice(0, 40);

      const order =
        (categories?.[categories.length - 1]?.order ?? categories.length) + 1;

      await setDoc(doc(db, "stores", user.uid, "categories", id), {
        name,
        order,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setNewCategoryName("");

      // reload categories
      const q = query(
        collection(db, "stores", user.uid, "categories"),
        orderBy("order", "asc")
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setCategories(list);
      setCategoryId(id);

      setMsg("✅ Category added.");
    } catch (e) {
      setMsg(e?.message || "Failed to add category.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- save product ---------------- */
  const onSave = async () => {
    if (!user) return setMsg("Please login first.");
    if (!store) return setMsg("Create a store first.");

    const t = safe(title);
    if (!t) return setMsg("Product title is required.");

    if (!safe(categoryId)) return setMsg("Please choose a category.");

    const p = moneyToNumber(price);
    const s = numToInt(stock);

    if (!Number.isFinite(p) || p <= 0) return setMsg("Enter a valid price.");
    if (!Number.isFinite(s) || s < 0) return setMsg("Enter a valid stock.");
    if (!images.length) return setMsg("Add at least 1 product image.");

    const thumbnailUrl = images[thumbIndex]?.url || images[0]?.url || "";

    const categoryName =
      safe(categories.find((c) => c.id === categoryId)?.name) || "";

    setSaving(true);
    setMsg("");

    try {
      await addDoc(collection(db, "products"), {
        storeId: user.uid,
        ownerId: user.uid,

        categoryId: safe(categoryId),
        categoryName,

        title: t,
        shortDescription: safe(description).slice(0, 120),
        description: safe(description),

        price: p,
        currency: safe(currency) || "GHS",

        hasDiscount: !!discountEnabled && computedDiscountPercent > 0,
        discountPercent: discountEnabled ? computedDiscountPercent : 0,

        stock: s,
        status: safe(status) || "ACTIVE",

        productType: safe(productType),

        thumbnailUrl,
        thumbnailIndex: thumbIndex,
        mediaUrls: images.map((x) => x.url).filter(Boolean),
        mediaCount: images.length,

        attributes: normalizedAttributes,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setMsg("✅ Product added!");

      // reset form
      setTitle("");
      setPrice("");
      setStock("1");
      setDescription("");
      setProductType("");

      setDiscountEnabled(false);
      setDiscountMode("PERCENT");
      setDiscountPercent("0");
      setDiscountFinalPrice("");

      setAttributes([{ key: "", value: "" }]);

      setImages([]);
      setThumbIndex(0);

      setCurrency("GHS");
      setStatus("ACTIVE");
    } catch (e) {
      setMsg(e?.message || "Failed to add product.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.top}>
          <button onClick={goBack} style={styles.backBtn}>
            ‹ Back
          </button>
          <div>
            <div style={styles.title}>Add New Product</div>
            <div style={styles.sub}>Store: {storeName}</div>
          </div>
        </div>

        {loading ? <div style={styles.alert}>Loading…</div> : null}
        {msg ? <div style={styles.alert}>{msg}</div> : null}

        {!loading && store ? (
          <div style={styles.form}>
            {/* Upload */}
            <div style={styles.uploadBox}>
              <div style={styles.uploadTop}>
                <div>
                  <div style={styles.boxTitle}>Product Images *</div>
                  <div style={styles.miniNote}>
                    Click + to choose photos (real upload).
                  </div>
                </div>

                <button
                  type="button"
                  onClick={openPicker}
                  disabled={uploading}
                  style={{
                    ...styles.plusBtn,
                    opacity: uploading ? 0.7 : 1,
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                  title="Upload images"
                >
                  {uploading ? `${uploadPct}%` : "＋"}
                </button>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onFileChange}
                  style={{ display: "none" }}
                />
              </div>

              {images.length ? (
                <div style={styles.imageGrid}>
                  {images.map((img, idx) => (
                    <div key={img.url} style={styles.imageCard}>
                      <img
                        src={img.url}
                        alt="product"
                        style={styles.img}
                        onClick={() => setThumbIndex(idx)}
                      />
                      <div style={styles.imageActions}>
                        <button
                          type="button"
                          onClick={() => setThumbIndex(idx)}
                          style={{
                            ...styles.pill,
                            background: idx === thumbIndex ? "#0f172a" : "#fff",
                            color: idx === thumbIndex ? "#fff" : "#0f172a",
                          }}
                        >
                          {idx === thumbIndex ? "Thumbnail" : "Set thumb"}
                        </button>

                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          style={styles.pillDanger}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyUpload}>
                  No images yet. Click the <b>＋</b> to upload.
                </div>
              )}
            </div>

            {/* Category */}
            <div style={styles.box}>
              <div style={styles.boxTop}>
                <div style={styles.boxTitle}>Category (seller-owned) *</div>
              </div>

              {isMobile ? (
                <MobileSheetSelect
                  label="Choose category"
                  value={categoryId}
                  onChange={(v) => setCategoryId(String(v))}
                  options={categoryOptions}
                  disabled={catsLoading}
                  placeholder="Choose category"
                />
              ) : (
                <label style={styles.label}>
                  Choose category
                  <select
                    style={styles.input}
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    disabled={catsLoading}
                  >
                    {categories.length ? (
                      categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {safe(c.name) || c.id}
                        </option>
                      ))
                    ) : (
                      <option value="">
                        {catsLoading
                          ? "Loading…"
                          : "No categories yet (add one below)"}
                      </option>
                    )}
                  </select>
                </label>
              )}

              <div style={styles.rowInline2}>
                <input
                  style={styles.input}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Create new category e.g. Dresses"
                />
                <button
                  type="button"
                  onClick={createCategory}
                  disabled={saving}
                  style={styles.smallBtn}
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Title */}
            <label style={styles.label}>
              Product Title *
              <input
                style={styles.input}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Blue Summer Dress"
              />
            </label>

            {/* Product Type */}
            <label style={styles.label}>
              Product Type (optional)
              <input
                style={styles.input}
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                placeholder="e.g. Dress / Slippers / iPhone 13"
              />
              <div style={styles.miniNote}>Seller can type anything. Not forced.</div>
            </label>

            {/* Price / Stock */}
            <div style={styles.row2}>
              <div style={{ display: "grid", gap: 6 }}>
                <div style={styles.labelText}>Price *</div>

                <div style={styles.rowInline}>
                  {isMobile ? (
                    <div style={{ minWidth: 110 }}>
                      <MobileSheetSelect
                        label=""
                        value={currency}
                        onChange={(v) => setCurrency(String(v))}
                        options={[
                          { value: "GHS", label: "GHS" },
                          { value: "USD", label: "USD" },
                          { value: "NGN", label: "NGN" },
                          { value: "EUR", label: "EUR" },
                          { value: "GBP", label: "GBP" },
                        ]}
                        placeholder="Currency"
                      />
                    </div>
                  ) : (
                    <select
                      style={{ ...styles.input, padding: "10px 10px" }}
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                    >
                      <option value="GHS">GHS</option>
                      <option value="USD"></option>
                      <option value="NGN"></option>
                      <option value="EUR"></option>
                      <option value="GBP"></option>
                    </select>
                  )}

                  <input
                    style={styles.input}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g. 250"
                    inputMode="decimal"
                  />
                </div>
              </div>

              <label style={styles.label}>
                Stock *
                <input
                  style={styles.input}
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                  placeholder="e.g. 10"
                  inputMode="numeric"
                />
              </label>
            </div>

            {/* Discount */}
            <div style={styles.box}>
              <div style={styles.boxTop}>
                <div style={styles.boxTitle}>Discount (optional)</div>

                <label style={styles.switchWrap}>
                  <input
                    type="checkbox"
                    checked={discountEnabled}
                    onChange={(e) => setDiscountEnabled(e.target.checked)}
                  />
                  <span style={styles.switchText}>Enable</span>
                </label>
              </div>

              {discountEnabled ? (
                <>
                  <div style={styles.row2}>
                    <div style={{ display: "grid", gap: 6 }}>
                      {isMobile ? (
                        <MobileSheetSelect
                          label="Discount Type"
                          value={discountMode}
                          onChange={(v) => setDiscountMode(String(v))}
                          options={[
                            { value: "PERCENT", label: "Percentage (%)" },
                            { value: "FINAL_PRICE", label: "Final Price" },
                          ]}
                          placeholder="Discount Type"
                        />
                      ) : (
                        <label style={styles.label}>
                          Discount Type
                          <select
                            style={styles.input}
                            value={discountMode}
                            onChange={(e) => setDiscountMode(e.target.value)}
                          >
                            <option value="PERCENT">Percentage (%)</option>
                            <option value="FINAL_PRICE">Final Price</option>
                          </select>
                        </label>
                      )}
                    </div>

                    {discountMode === "PERCENT" ? (
                      <label style={styles.label}>
                        Discount %
                        <input
                          style={styles.input}
                          value={discountPercent}
                          onChange={(e) => setDiscountPercent(e.target.value)}
                          placeholder="e.g. 10"
                          inputMode="numeric"
                        />
                      </label>
                    ) : (
                      <label style={styles.label}>
                        Discounted Price
                        <input
                          style={styles.input}
                          value={discountFinalPrice}
                          onChange={(e) => setDiscountFinalPrice(e.target.value)}
                          placeholder="e.g. 150"
                          inputMode="decimal"
                        />
                      </label>
                    )}
                  </div>

                  <div style={styles.miniNote}>
                    Saved as <b>discountPercent</b>: {computedDiscountPercent}%
                  </div>
                </>
              ) : (
                <div style={styles.miniNote}>Leave off if no discount.</div>
              )}
            </div>

            {/* Description */}
            <label style={styles.label}>
              Description (optional)
              <textarea
                style={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Write details buyers should know…"
              />
            </label>

            {/* Custom fields */}
            <div style={styles.box}>
              <div style={styles.boxTop}>
                <div style={styles.boxTitle}>Custom Details (seller adds fields)</div>

                <button type="button" onClick={addAttrRow} style={styles.smallBtn}>
                  + Add field
                </button>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                {attributes.map((row, idx) => (
                  <div key={idx} style={styles.attrRow}>
                    <input
                      style={styles.input}
                      value={row.key}
                      onChange={(e) => updateAttrRow(idx, { key: e.target.value })}
                      placeholder="Field name (e.g. Size, Color, Engine, Author)"
                    />
                    <input
                      style={styles.input}
                      value={row.value}
                      onChange={(e) =>
                        updateAttrRow(idx, { value: e.target.value })
                      }
                      placeholder="Value (e.g. 30-50, Black, 2.0L)"
                    />
                    {attributes.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => removeAttrRow(idx)}
                        style={styles.trashBtn}
                        title="Remove"
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>

              <div style={styles.miniNote}>
                Seller can add unlimited fields for ANY product type.
              </div>
            </div>

            {/* Status */}
            {isMobile ? (
              <MobileSheetSelect
                label="Status"
                value={status}
                onChange={(v) => setStatus(String(v))}
                options={[
                  { value: "ACTIVE", label: "ACTIVE (Visible)" },
                  { value: "DRAFT", label: "DRAFT (Hidden)" },
                ]}
                placeholder="Status"
              />
            ) : (
              <label style={styles.label}>
                Status
                <select
                  style={styles.input}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="ACTIVE">ACTIVE (Visible)</option>
                  <option value="DRAFT">DRAFT (Hidden)</option>
                </select>
              </label>
            )}

            <button
              onClick={onSave}
              disabled={saving || uploading}
              style={{
                ...styles.saveBtn,
                opacity: saving || uploading ? 0.7 : 1,
                cursor: saving || uploading ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Saving…" : uploading ? "Uploading images…" : "✅ Save Product"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ---------------- styles ---------------- */
const styles = {
  page: {
    minHeight: "100vh",
    background: "#f3f6ff",
    padding: 16,
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    color: "#0f172a",
  },
  card: {
    maxWidth: 760,
    margin: "0 auto",
    background: "#fff",
    border: "1px solid #e7ecf6",
    borderRadius: 18,
    boxShadow: "0 16px 45px rgba(15,23,42,0.08)",
    padding: 14,
  },
  top: { display: "flex", gap: 12, alignItems: "center" },
  backBtn: {
    border: "1px solid #e7ecf6",
    background: "#f7fbff",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    cursor: "pointer",
  },
  title: { fontSize: 18, fontWeight: 1000 },
  sub: { marginTop: 2, color: "#64748b", fontWeight: 900, fontSize: 12 },

  alert: {
    marginTop: 10,
    background: "#fbfdff",
    border: "1px solid #eef2ff",
    borderRadius: 12,
    padding: 10,
    fontWeight: 900,
  },
  form: { marginTop: 12, display: "grid", gap: 12 },

  label: { display: "grid", gap: 6, fontWeight: 950, fontSize: 13 },
  labelText: { fontWeight: 950, fontSize: 13 },

  input: {
    border: "1px solid #e7ecf6",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    outline: "none",
    width: "100%",
  },
  textarea: {
    border: "1px solid #e7ecf6",
    borderRadius: 12,
    padding: "10px 12px",
    fontWeight: 900,
    outline: "none",
    minHeight: 90,
    resize: "vertical",
    width: "100%",
  },

  row2: { display: "grid", gap: 12, gridTemplateColumns: "1fr 1fr" },
  rowInline: { display: "grid", gridTemplateColumns: "110px 1fr", gap: 10 },
  rowInline2: { display: "grid", gridTemplateColumns: "1fr 110px", gap: 10 },

  saveBtn: {
    border: 0,
    background: "#0f172a",
    color: "#fff",
    borderRadius: 14,
    padding: "12px 12px",
    fontWeight: 1000,
  },

  box: {
    border: "1px solid #eef2ff",
    background: "#fbfdff",
    borderRadius: 14,
    padding: 12,
    display: "grid",
    gap: 10,
  },
  boxTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  boxTitle: { fontWeight: 1000, fontSize: 13 },
  miniNote: { fontSize: 12, color: "#64748b", fontWeight: 800 },

  switchWrap: { display: "flex", alignItems: "center", gap: 8, fontWeight: 900 },
  switchText: { fontSize: 12, color: "#0f172a" },

  smallBtn: {
    border: "1px solid #e7ecf6",
    background: "#fff",
    borderRadius: 10,
    padding: "8px 10px",
    fontWeight: 950,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  attrRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr 40px",
    gap: 10,
    alignItems: "center",
  },
  trashBtn: {
    border: "1px solid #e7ecf6",
    background: "#fff",
    borderRadius: 10,
    height: 42,
    fontWeight: 1000,
    cursor: "pointer",
  },

  uploadBox: {
    border: "1px dashed #cfd7ee",
    background: "#fbfdff",
    borderRadius: 14,
    padding: 12,
    display: "grid",
    gap: 12,
  },
  uploadTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  plusBtn: {
    width: 52,
    height: 52,
    borderRadius: 14,
    border: "1px solid #e7ecf6",
    background: "#0f172a",
    color: "#fff",
    fontSize: 28,
    fontWeight: 1000,
    lineHeight: "52px",
  },
  emptyUpload: {
    border: "1px solid #eef2ff",
    background: "#fff",
    borderRadius: 12,
    padding: 12,
    fontWeight: 900,
    color: "#64748b",
  },
  imageGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
  },
  imageCard: {
    border: "1px solid #e7ecf6",
    borderRadius: 14,
    background: "#fff",
    overflow: "hidden",
    display: "grid",
    gap: 8,
    paddingBottom: 10,
  },
  img: {
    width: "100%",
    height: 140,
    objectFit: "cover",
    cursor: "pointer",
    display: "block",
  },
  imageActions: {
    display: "flex",
    gap: 8,
    padding: "0 10px",
    flexWrap: "wrap",
  },
  pill: {
    border: "1px solid #e7ecf6",
    background: "#fff",
    borderRadius: 999,
    padding: "8px 10px",
    fontWeight: 950,
    cursor: "pointer",
  },
  pillDanger: {
    border: "1px solid #ffe0e0",
    background: "#fff5f5",
    borderRadius: 999,
    padding: "8px 10px",
    fontWeight: 950,
    cursor: "pointer",
  },
};
